import React, { useEffect, useState, useRef } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { formatDate, convertTime, downloadCsv } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

import { GetReport, GetReportRun, GetReportStatus } from "api/rpc/2024-04/facilityAdmin/report/report";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import RoundsByCustomerTypeTable from "../tables/RoundsByCustomerTypeTable";
import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { closeInfo, showError, showInfo, showSuccess } from "redux/actions/ui";
import moment from "moment";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import { Pie } from "react-chartjs-2";
import { DASHBOARD_COLOURS } from "../../dashboard/dashboardNew/dashboardWidgets/bookingType/BookingType";
import { delay } from "components/_util/delay";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import TimePick from "components/timePick/TimePick";
import { ButtonNew } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown, faSave } from "@fortawesome/pro-regular-svg-icons";
import { ColumnInstance } from "react-table";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  compareDateRangeStart: Date;
  compareDateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  compare_to_date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  startTime: string;
  endTime: string;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function RoundsByCustomerType() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const { t } = useTranslation();
  const [filteredColumns, setFilteredColumns] = useState<Array<ColumnInstance<Record<string, any>>>>([]);
  const [filteredCompareColumns, setFilteredCompareColumns] = useState<Array<ColumnInstance<Record<string, any>>>>([]);
  const _APIPollFlag = useRef(false);
  const [pollCount, setPollCount] = useState<number>(0);
  const chartRef = useRef(null);
  const compareRef = useRef(null);

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    compareDateRangeStart: today,
    compareDateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    compare_to_date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
    startTime: null,
    endTime: null,
  });

  const [lastRunState, setLastRunState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    compareDateRangeStart: today,
    compareDateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    compare_to_date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
    startTime: null,
    endTime: null,
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  const [compareToData, setCompareToData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];
    const sortedConfigurations = [...report.configurations].sort((prev, next) => prev.position - next.position);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      configurations: sortedConfigurations,
    }));
  }

  async function getReport(email: string) {
    setReportData(prev => ({
      ...prev,
      rows: [],
      columns: [],
      description: null,
    }));
    setCompareToData(prev => ({
      ...prev,
      rows: [],
      columns: [],
      description: null,
    }));

    dispatch(showInfo("Loading report...", null, true, "Your report is being prepared. This may take a few minutes"));

    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        compare_to_date_range: state.compare_to_date_range,
        should_queue: true,
        time_range: state.startTime === null || state.endTime === null ? undefined : [state.startTime, state.endTime],
        email,
      },
      false,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(closeInfo());
      dispatch(showError(reportRes.message));
      return;
    }

    _APIPollFlag.current = true;
    void pollReport(reportRes.data?.executed_report_id as number);

    setLastRunState(state);

    return;
  }

  const pollReport = async (executed_report_id: number) => {
    if (executed_report_id) {
      const res = await GetReportStatus({ executed_report_id }, false);
      console.log("Polling res", res);
      if (res.status === StatusCode.OK && res.data?.status === "completed") {
        setPollCount(0);
        _APIPollFlag.current = false;
        const data = JSON.parse(res.data.report_data as string);
        setReportData(prev => ({
          ...prev,
          rows: data.date_range_1?.rows,
          columns: data.date_range_1?.columns,
          description: data?.description ?? "",
        }));
        if (data.date_range_2) {
          setCompareToData(prev => ({
            ...prev,
            rows: data.date_range_2?.rows,
            columns: data.date_range_2?.columns,
            description: data?.description ?? "",
          }));
        }
        dispatch(closeInfo());
      } else {
        if (_APIPollFlag.current === true) {
          setPollCount(prev => prev + 1);
          await delay(pollCount > 5 ? 5000 : 1000);
          await pollReport(executed_report_id);
        }
      }
    } else {
      _APIPollFlag.current = false;
    }
  };

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  useEffect(() => {
    if (state.compareDateRangeStart && state.compareDateRangeEnd) {
      const date_range = formatDateRange(state.compareDateRangeStart, state.compareDateRangeEnd);
      setState(prevState => ({ ...prevState, compare_to_date_range: date_range }));
    }
  }, [state.compareDateRangeStart, state.compareDateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  function setCompareDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      compareDateRangeStart: date,
    }));
  }

  function setCompareDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      compareDateRangeEnd: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.rounds_by_customer_type.003"),
    action: () => {
      if (reportShouldQueue()) {
        dispatch(showSuccess(
          t('Potential large report detected.'),
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function escapeCsvValue(value: unknown): string {
    let stringValue = "";

    if (typeof value === "string") {
      stringValue = value;
    } else if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
      stringValue = value.toString();
    }

    if (/[",\n\r]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  }

  function getExportColumns(
    tableData: IReportDataState,
    visibleColumns: Array<ColumnInstance<Record<string, any>>>,
  ): Array<Record<string, any>> {
    if (visibleColumns?.length > 0) {
      const visibleColumnIds = new Set(visibleColumns.map(column => column.id));
      return tableData.columns.filter(column => visibleColumnIds.has(column.id as string));
    }

    return tableData.columns;
  }

  function exportTable(
    tableData: IReportDataState,
    visibleColumns: Array<ColumnInstance<Record<string, any>>>,
    filenameSuffix?: string,
  ) {
    const columns = getExportColumns(tableData, visibleColumns);
    const rows = tableData?.rows?.filter((row: Record<string, any>) => !row.is_footer) ?? [];

    if (!columns?.length || !rows.length) {
      dispatch(showError(t("secure.facility.reports.pages.rounds_by_customer_type.001")));
      return;
    }

    const headerRow = columns.map(column => escapeCsvValue(column.display ?? column.id)).join(",");
    const dataRows = rows.map((row: Record<string, any>) =>
      columns.map(column => escapeCsvValue(row[column.id])).join(","),
    );
    const footerRows = (tableData.rows ?? [])
      .filter((row: Record<string, any>) => row.is_footer === true)
      .map((row: Record<string, any>) => columns.map(column => escapeCsvValue(row[column.id])).join(","));

    if (footerRows.length === 0) {
      footerRows.push(
        columns
          .map(column => {
            if (column.id === "date") {
              return escapeCsvValue(t("Total"));
            }

            const total = rows.reduce((sum: number, row: Record<string, any>) => {
              return sum + (Number(row[column.id]) || 0);
            }, 0);

            return escapeCsvValue(total);
          })
          .join(","),
      );
    }

    const csv = [headerRow, ...dataRows, ...footerRows].join("\n");
    const filename = filenameSuffix ? `${reportFilename()}_${filenameSuffix}.csv` : `${reportFilename()}.csv`;
    downloadCsv(csv, filename);
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function getChartData(reportData: any, compareTo: boolean): Array<{ label: string, rounds: number }> {
    const data = reportData?.columns
      ?.filter((column: any) => column.id !== "date" && column.id !== "Total")
      ?.filter((column: any) => (compareTo ? filteredCompareColumns : filteredColumns).find((filteredColumn: any) => filteredColumn.id === column.id))
      ?.map((column: any) => (
        {
          label: column?.id,
          rounds: reportData?.rows?.reduce((prev: number, current: any) => {
            return prev + Number(current[column.id]);
          }, 0),
        }
      ));

    return data as Array<{ label: string, rounds: number }>;
  }

  function formatChartTitle(compareTo: boolean): string {
    const startDate = compareTo ? lastRunState.compareDateRangeStart : lastRunState.dateRangeStart;
    const endDate = compareTo ? lastRunState.compareDateRangeEnd : lastRunState.dateRangeEnd;
    const startTime = lastRunState.startTime ? "(" + convertTime(lastRunState.startTime) : lastRunState.endTime ? t("(Start of day") : "";
    const endTime = lastRunState.endTime ? " - " + convertTime(lastRunState.endTime) + ")" : lastRunState.startTime ? t(" - End of day)") : "";
    return `${moment(startDate).format("MMMM Do YYYY")} - ${moment(endDate).format("MMMM Do YYYY")} ${startTime} ${endTime}`;
  }

  const downloadChart = (ref: any) => {
    if (!ref.current) {
      dispatch(showError("Chart does not exist"));
      return;
    }

    const image = ref.current.toBase64Image();

    const link = document.createElement('a');
    link.href = image;
    link.download = reportFilename() + '.png';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const whiteBackgroundPlugin = {
    id: 'whiteBackground',
    beforeDraw: (chart: any) => {
      const { ctx } = chart;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  };

  return (
    <Page
      title={t("secure.facility.reports.pages.rounds_by_customer_type.004")}
      primaryAction={primaryAction}
      breadcrumbs={[
        { prefix: true, label: t("secure.facility.reports.pages.rounds_by_customer_type.008"), url: "/admin/report" },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              label={t("Date Range")}
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <DatePickerInput
              label={t("Compare To Date Range")}
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.compareDateRangeStart}
              setStartingDate={setCompareDateRangeStart}
              endingDate={state.compareDateRangeEnd}
              setEndingDate={setCompareDateRangeEnd}
            />
            <div className="w-25 flex gap-3">
              <TimePick
                value={state.startTime}
                onChange={timeString => setState(prev => ({ ...prev, startTime: timeString }))}
                label={t("Start Time (optional)")}
                size="large"
              />
              <TimePick
                value={state.endTime}
                onChange={timeString => setState(prev => ({ ...prev, endTime: timeString }))}
                label={t("End Time (optional)")}
                size="large"
              />
            </div>
          </div>
        </Card.Section>
      </Card>


      <div className="flex gap-2 justify-between pt-4">
        {reportData?.columns?.length > 0 ? (
          <div className="flex flex-col gap-2 items-center w-50">
            <div className="pie-chart-container" style={{ width: "100%" }}>
              <Pie
                ref={chartRef}
                data={{
                  labels: getChartData(reportData, false).map(data => data.label),
                  datasets: [
                    {
                      label: t("# of rounds"),
                      data: getChartData(reportData, false).map(data => data.rounds),
                      backgroundColor: getChartData(reportData, false).map((data, index) => DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length]),
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  aspectRatio: 2,
                  plugins: {
                    legend: {
                      position: "left",
                      title: {
                        text: t("Type"),
                        display: true,
                      },
                      labels: {
                        boxWidth: 12,
                      },
                    },
                    datalabels: {
                      display: true,
                      color: "white",
                      formatter: (value, ctx) => {
                        let sum = 0;
                        const dataArr = ctx.chart.data.datasets[0].data;
                        dataArr.map(data => {
                          sum += data as number;
                        });
                        const percentage = (value * 100 / sum).toFixed(2) + "%";
                        return percentage;
                      },
                    },
                    title: {
                      display: true,
                      text: formatChartTitle(false),
                      align: "start",
                      font: {
                        size: 16,
                        weight: "bold",
                      },
                    },
                  },
                }}
                plugins={[ChartDataLabels, whiteBackgroundPlugin]}
              />
              <div className="pie-chart-icon">
                <ButtonNew
                  onClick={() => downloadChart(chartRef)}
                  type="link"
                >
                  <FontAwesomeIcon icon={faSave} />
                </ButtonNew>
              </div>
            </div>

            <div className="dynamic-table-container w-full">
              <RoundsByCustomerTypeTable
                data={reportData.rows}
                columns={reportData.columns}
                description={reportData.description}
                compileDate={reportData.compileDate}
                setFilteredColumns={setFilteredColumns}
                toolbarActions={
                  <div
                    className="table-filter-dropdown-button"
                    onClick={() => exportTable(reportData, filteredColumns)}
                  >
                    <FontAwesomeIcon icon={faFileArrowDown} />
                    {t("secure.facility.reports.pages.rounds_by_customer_type.006")}
                  </div>
                }
              />
            </div>
          </div>
        ) : null}
        {compareToData?.columns?.length > 0 ? (
          <div className="flex flex-col gap-2 items-center w-50">
            <div className="pie-chart-container" style={{ width: "100%" }}>
              <Pie
                ref={compareRef}
                data={{
                  labels: getChartData(compareToData, true).map(data => data.label),
                  datasets: [
                    {
                      label: t("# of rounds"),
                      data: getChartData(compareToData, true).map(data => data.rounds),
                      backgroundColor: getChartData(compareToData, true).map((data, index) => DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length]),
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  aspectRatio: 2,
                  plugins: {
                    legend: {
                      position: "left",
                      title: {
                        text: t("Type"),
                        display: true,
                      },
                      labels: {
                        boxWidth: 12,
                      },
                    },
                    datalabels: {
                      display: true,
                      color: "white",
                      formatter: (value, ctx) => {
                        let sum = 0;
                        const dataArr = ctx.chart.data.datasets[0].data;
                        dataArr.map(data => {
                          sum += data as number;
                        });
                        const percentage = (value * 100 / sum).toFixed(2) + "%";
                        return percentage;
                      },
                    },
                    title: {
                      display: true,
                      text: formatChartTitle(true),
                      align: "start",
                      font: {
                        size: 16,
                        weight: "bold",
                      },
                    },
                  },
                }}
                plugins={[ChartDataLabels, whiteBackgroundPlugin]}
              />
              <div className="pie-chart-icon">
                <ButtonNew
                  onClick={() => downloadChart(chartRef)}
                  type="link"
                >
                  <FontAwesomeIcon icon={faSave} />
                </ButtonNew>
              </div>
            </div>

            <div className="dynamic-table-container w-full">
              <RoundsByCustomerTypeTable
                data={compareToData.rows}
                columns={compareToData.columns}
                description={compareToData.description}
                compileDate={compareToData.compileDate}
                setFilteredColumns={setFilteredCompareColumns}
                toolbarActions={
                  <div
                    className="table-filter-dropdown-button"
                    onClick={() => exportTable(compareToData, filteredCompareColumns, "compare")}
                  >
                    <FontAwesomeIcon icon={faFileArrowDown} />
                    {t("secure.facility.reports.pages.rounds_by_customer_type.006")}
                  </div>
                }
              />
            </div>
          </div>
        ) : null}
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
